(() => {
  if (customElements.get("article-table-of-contents")) return;

  class ArticleTableOfContents extends HTMLElement {
    constructor() {
      super();
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.initAttempts = 0;
      this.maxInitAttempts = 30;
      this.mediaQuery = window.matchMedia("(min-width: 990px)");
      this.scrollObserver = null;
      this.tocItems = [];
      this.lastActiveHeading = null;
      this.defaultHeading = "";
      this.headingEl = null;
      this.navEl = null;
      this.offsetResizeObserver = null;
      this.offsetResizeTimeout = null;
      this.offsetScrollRaf = 0;
      this.offsetTrackingEnabled = false;
      this.onOffsetResize = null;
      this.onOffsetScroll = null;
      this.headerClassObserver = null;
      this.onWindowLoad = null;
      this.onHeaderSectionLoad = null;
      this.stickySentinel = null;
      this.stickyStateObserver = null;
      this.stickyStateRaf = 0;
      this.stickyHost = null;
      this.onNavClick = this.onNavClick.bind(this);
      this.headingsForSpy = [];
      this.scrollSpyRaf = 0;
      this.onScrollSpyScroll = null;
      this.scrollClickLock = null;
      this.scrollClickTarget = null;
      this.scrollClickUnlockRaf = 0;
      this.onDetailsToggle = this.onDetailsToggle.bind(this);
      this.detailsEl = null;
    }

    connectedCallback() {
      this.defaultHeading = this.dataset.defaultHeading || "";
      this.headingEl = this.querySelector(".js-article-toc-heading");
      this.navEl = this.querySelector(".js-article-toc-nav");
      this.stickyHost = this.closest(".article-toc-sticky-host");
      this.navEl?.addEventListener("click", this.onNavClick);
      this.setupOffsetTracking();
      this.init();
      this.mediaQuery.addEventListener("change", this.onMediaChange);
      document.addEventListener("shopify:section:load", this.onSectionLoad);
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener("change", this.onMediaChange);
      document.removeEventListener("shopify:section:load", this.onSectionLoad);
      this.scrollObserver?.disconnect();
      this.offsetResizeObserver?.disconnect();
      this.headerClassObserver?.disconnect();
      if (this.onOffsetResize) {
        window.removeEventListener("resize", this.onOffsetResize);
      }
      if (this.onOffsetScroll) {
        window.removeEventListener("scroll", this.onOffsetScroll);
      }
      if (this.onWindowLoad) {
        window.removeEventListener("load", this.onWindowLoad);
      }
      document.removeEventListener("shopify:section:load", this.onHeaderSectionLoad);
      if (this.offsetResizeTimeout) {
        clearTimeout(this.offsetResizeTimeout);
      }
      if (this.offsetScrollRaf) {
        cancelAnimationFrame(this.offsetScrollRaf);
      }
      if (this.stickyStateRaf) {
        cancelAnimationFrame(this.stickyStateRaf);
      }
      if (this.scrollSpyRaf) {
        cancelAnimationFrame(this.scrollSpyRaf);
      }
      if (this.scrollClickUnlockRaf) {
        cancelAnimationFrame(this.scrollClickUnlockRaf);
      }
      if (this.onScrollSpyScroll) {
        window.removeEventListener("scroll", this.onScrollSpyScroll);
        this.onScrollSpyScroll = null;
      }
      this.scrollClickLock = null;
      this.scrollClickTarget = null;
      this.detailsEl?.removeEventListener("toggle", this.onDetailsToggle);
      this.detailsEl = null;
      this.stickyStateObserver?.disconnect();
      this.stickySentinel?.remove();
      this.stickySentinel = null;
      this.stickyHost = null;
      this.offsetTrackingEnabled = false;
      this.navEl?.removeEventListener("click", this.onNavClick);
    }

    onSectionLoad(event) {
      const section = this.closest("[id^='shopify-section-']");
      if (!section || section.id !== `shopify-section-${event.detail.sectionId}`) return;
      this.initAttempts = 0;
      this.init();
    }

    onMediaChange() {
      this.setupDetails();
      this.updateStickyState();
      this.scrollClickLock = null;
      if (this.headingsForSpy.length) {
        this.setupScrollSpy(this.headingsForSpy);
      }
      if (this.mediaQuery.matches) {
        this.updateDynamicHeading(null);
      } else if (this.lastActiveHeading?.isConnected) {
        this.setActiveState(this.lastActiveHeading);
      } else {
        this.updateDynamicHeading(null);
      }
    }

    init() {
      const article = this.closest(".article-template");
      const list = this.querySelector(".js-article-toc-list");

      if (!article || !list) {
        this.hide();
        return;
      }

      const content = article.querySelector("[data-js-article-content]");
      if (!content) {
        this.initAttempts += 1;
        if (this.initAttempts < this.maxInitAttempts) {
          requestAnimationFrame(() => this.init());
        } else {
          this.hide();
        }
        return;
      }

      this.ensureBodyLayout(article, content);
      this.initAttempts = 0;
      this.setupStickyStateObserver();
      this.build(content, list);
      this.setupDetails();
      this.handleOffsetChange();
    }

    ensureStickyHost() {
      if (this.stickyHost?.isConnected) {
        return this.stickyHost;
      }

      if (this.parentElement?.classList.contains("article-toc-sticky-host")) {
        this.stickyHost = this.parentElement;
        return this.stickyHost;
      }

      const host = document.createElement("div");
      host.className = "article-toc-sticky-host";
      const parent = this.parentElement;

      if (parent) {
        parent.insertBefore(host, this);
        host.appendChild(this);
      }

      this.stickyHost = host;
      return this.stickyHost;
    }

    getStickyHost() {
      return this.stickyHost || this.ensureStickyHost();
    }

    ensureBodyLayout(article, content) {
      let body = article.querySelector(".article-template-body");
      const articlePositionClass = [...article.classList].find((className) =>
        className.startsWith("article-template--toc-"),
      );
      const bodyPositionClass = articlePositionClass
        ? articlePositionClass.replace("article-template--", "article-template-body--")
        : "article-template-body--toc-left";
      const stickyHost = this.getStickyHost();

      if (!body) {
        body = document.createElement("div");
        body.className = `article-template-body ${bodyPositionClass}`;
        this.insertBodyBeforeFirstElement(body, article, stickyHost, content);
      } else {
        body.classList.remove("article-template-body--toc-left", "article-template-body--toc-right");
        body.classList.add(bodyPositionClass);
      }

      if (stickyHost.parentElement !== body) {
        body.appendChild(stickyHost);
      }

      if (content.parentElement !== body) {
        body.appendChild(content);
      }
    }

    insertBodyBeforeFirstElement(body, article, tocElement, contentElement) {
      let insertBeforeElement = tocElement;
      if (tocElement.compareDocumentPosition(contentElement) & Node.DOCUMENT_POSITION_PRECEDING) {
        insertBeforeElement = contentElement;
      }
      article.insertBefore(body, insertBeforeElement);
    }

    build(content, list) {
      const headings = content.querySelectorAll("h2, h3");
      const slugCounts = new Map();
      const items = [];
      let lastH2 = null;

      list.replaceChildren();
      this.scrollObserver?.disconnect();
      this.tocItems = [];
      this.lastActiveHeading = null;
      this.clearActiveLinks();
      this.updateDynamicHeading(null);

      headings.forEach((heading) => {
        const text = heading.textContent.replace(/\s+/g, " ").trim();
        if (!text) return;

        const level = heading.tagName.toLowerCase();
        if (level === "h2") {
          lastH2 = heading;
        }

        const id = this.ensureHeadingId(heading, text, slugCounts);
        const sectionH2 = level === "h2" ? heading : lastH2;
        items.push({ heading, id, text, level, sectionH2 });
      });

      if (items.length === 0) {
        this.hide();
        return;
      }

      this.hidden = false;
      this.getStickyHost().hidden = false;
      this.tocItems = items;

      items.forEach(({ id, text, level }) => {
        const item = document.createElement("li");
        item.className = `article-toc__item article-toc__item--${level}`;

        const link = document.createElement("a");
        link.className = "article-toc__link js-article-toc-link";
        link.href = `#${id}`;
        link.textContent = text;

        item.appendChild(link);
        list.appendChild(item);
      });

      this.setupScrollSpy(items.map((item) => item.heading));
    }

    ensureHeadingId(heading, text, slugCounts) {
      if (heading.id) {
        this.registerSlug(heading.id, slugCounts);
        return heading.id;
      }

      const baseSlug = this.slugify(text) || "section";
      const count = slugCounts.get(baseSlug) || 0;
      const id = count === 0 ? baseSlug : `${baseSlug}-${count}`;

      slugCounts.set(baseSlug, count + 1);
      heading.id = id;

      return id;
    }

    registerSlug(id, slugCounts) {
      const baseSlug = id.replace(/-\d+$/, "") || id;
      const count = slugCounts.get(baseSlug) || 0;
      slugCounts.set(baseSlug, count + 1);
    }

    slugify(text) {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    }

    setupDetails() {
      const details = this.querySelector(".js-article-toc-details");
      if (!details) return;

      if (this.detailsEl !== details) {
        this.detailsEl?.removeEventListener("toggle", this.onDetailsToggle);
        this.detailsEl = details;
        this.detailsEl.addEventListener("toggle", this.onDetailsToggle);
      }

      const mobileCollapsed = this.dataset.mobileCollapsed === "true";

      if (this.mediaQuery.matches) {
        details.setAttribute("open", "");
        return;
      }

      if (mobileCollapsed) {
        details.removeAttribute("open");
      } else {
        details.setAttribute("open", "");
      }
    }

    onDetailsToggle() {
      if (this.mediaQuery.matches || this.scrollClickLock) return;

      requestAnimationFrame(() => {
        if (this.scrollClickLock) return;

        if (this.lastActiveHeading?.isConnected) {
          this.setActiveState(this.lastActiveHeading);
          return;
        }

        this.syncActiveHeadingFromScroll();
      });
    }

    onNavClick(event) {
      const link = event.target.closest(".js-article-toc-link");
      if (!link || !this.navEl?.contains(link)) return;

      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#")) return;

      const targetId = href.slice(1);
      if (!targetId) return;

      const targetHeading = document.getElementById(targetId);
      if (!targetHeading) return;

      event.preventDefault();

      const isDesktop = this.mediaQuery.matches;

      if (!isDesktop) {
        const details = this.querySelector(".js-article-toc-details");
        details?.removeAttribute("open");
        this.lockScrollClickTarget(targetHeading, targetId);
      }

      requestAnimationFrame(() => {
        this.scrollToHeading(targetHeading, { isDesktop });
        if (!isDesktop) {
          this.beginScrollClickUnlock(targetHeading);
        }
      });
    }

    getScrollOffsetForTarget(isDesktop = this.mediaQuery.matches) {
      const stickyTopOffset = this.getStickyTopOffsetCSSValue();
      const stickyBarHeight = isDesktop ? 0 : this.getStickyBarHeight();
      const additionalBuffer = isDesktop ? 32 : 20;
      const total = stickyTopOffset + stickyBarHeight + additionalBuffer;

      return {
        stickyTopOffset,
        stickyBarHeight,
        additionalBuffer,
        total,
      };
    }

    getScrollTopForHeading(targetHeading, isDesktop = this.mediaQuery.matches) {
      const { total } = this.getScrollOffsetForTarget(isDesktop);
      const targetTop = targetHeading.getBoundingClientRect().top + window.scrollY;

      return Math.max(targetTop - total, 0);
    }

    scrollToHeading(targetHeading, { isDesktop = this.mediaQuery.matches } = {}) {
      const scrollTop = this.getScrollTopForHeading(targetHeading, isDesktop);

      window.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }

    lockScrollClickTarget(targetHeading, targetId) {
      this.scrollClickTarget = targetHeading;
      this.scrollClickLock = {
        heading: targetHeading,
        id: targetId,
        startedAt: performance.now(),
      };
      this.setActiveState(targetHeading);
    }

    beginScrollClickUnlock(targetHeading) {
      if (this.scrollClickUnlockRaf) {
        cancelAnimationFrame(this.scrollClickUnlockRaf);
      }

      const startedAt = performance.now();
      const maxDuration = 2500;
      const tolerance = 4;
      let lastScrollY = window.scrollY;
      let stableFrames = 0;

      const finishUnlock = () => {
        this.scrollClickUnlockRaf = 0;
        const scrollTarget = this.scrollClickTarget;
        this.scrollClickLock = null;
        this.confirmActiveStateAfterScroll(scrollTarget);
        this.scrollClickTarget = null;
      };

      const step = () => {
        if (!this.scrollClickLock || this.mediaQuery.matches) {
          finishUnlock();
          return;
        }

        const expectedScrollTop = this.getScrollTopForHeading(targetHeading, false);
        const currentScrollY = window.scrollY;
        const nearTarget = Math.abs(currentScrollY - expectedScrollTop) <= tolerance;
        const scrollDelta = Math.abs(currentScrollY - lastScrollY);

        if (scrollDelta < 1) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastScrollY = currentScrollY;
        }

        if (nearTarget || stableFrames >= 4 || performance.now() - startedAt >= maxDuration) {
          finishUnlock();
          return;
        }

        this.scrollClickUnlockRaf = requestAnimationFrame(step);
      };

      this.scrollClickUnlockRaf = requestAnimationFrame(step);
    }

    getStickyBarHeight() {
      const stickyHost = this.getStickyHost();
      if (!stickyHost) return 0;

      return Math.ceil(stickyHost.getBoundingClientRect().height);
    }

    setupOffsetTracking() {
      if (this.offsetTrackingEnabled) {
        this.handleOffsetChange();
        return;
      }

      this.offsetTrackingEnabled = true;

      this.onOffsetResize = () => {
        clearTimeout(this.offsetResizeTimeout);
        this.offsetResizeTimeout = setTimeout(() => this.handleOffsetChange(), 100);
      };

      this.onOffsetScroll = () => {
        if (this.offsetScrollRaf) return;
        this.offsetScrollRaf = requestAnimationFrame(() => {
          this.offsetScrollRaf = 0;
          this.handleOffsetChange();
        });
      };

      this.onHeaderSectionLoad = () => {
        requestAnimationFrame(() => this.handleOffsetChange());
      };

      this.onWindowLoad = () => {
        this.handleOffsetChange();
      };

      window.addEventListener("resize", this.onOffsetResize);
      window.addEventListener("scroll", this.onOffsetScroll, { passive: true });
      window.addEventListener("load", this.onWindowLoad);
      document.addEventListener("shopify:section:load", this.onHeaderSectionLoad);

      if ("ResizeObserver" in window) {
        this.offsetResizeObserver = new ResizeObserver(() => this.handleOffsetChange());

        this.getHeaderChromeElements().forEach((element) => {
          this.offsetResizeObserver.observe(element);
        });

        const stickyHeader = document.querySelector("sticky-header:not([disabled])");
        if (stickyHeader) {
          this.offsetResizeObserver.observe(stickyHeader);

          stickyHeader.querySelectorAll("#header, .header, .head-mast-wrap, .header-search-global").forEach((element) => {
            this.offsetResizeObserver.observe(element);
          });
        }
      }

      const sectionHeader = this.getSectionHeaderElement();
      if (sectionHeader) {
        this.headerClassObserver = new MutationObserver(() => this.handleOffsetChange());
        this.headerClassObserver.observe(sectionHeader, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }

      requestAnimationFrame(() => this.handleOffsetChange());
      setTimeout(() => this.handleOffsetChange(), 200);
      setTimeout(() => this.handleOffsetChange(), 600);
    }

    handleOffsetChange() {
      this.updateStickyTopOffset();
      this.updateStickyState();

      if (this.mediaQuery.matches || !this.headingsForSpy.length || this.scrollClickLock) {
        return;
      }

      if (this.detailsEl?.open && this.lastActiveHeading?.isConnected) {
        this.setActiveState(this.lastActiveHeading);
        return;
      }

      this.syncActiveHeadingFromScroll();
    }

    setupStickyStateObserver() {
      const body = this.closest(".article-template-body");
      const stickyHost = this.getStickyHost();
      if (!body || !stickyHost) return;

      if (!this.stickySentinel) {
        this.stickySentinel = document.createElement("div");
        this.stickySentinel.className = "article-toc-sticky-sentinel";
        this.stickySentinel.setAttribute("aria-hidden", "true");
      }

      if (this.stickySentinel.parentElement !== body) {
        body.insertBefore(this.stickySentinel, stickyHost);
      }

      stickyHost.classList.remove("is-sticky-active");

      this.stickyStateObserver?.disconnect();
      this.stickyStateObserver = new IntersectionObserver(() => this.updateStickyState(), {
        threshold: [0, 1],
      });
      this.stickyStateObserver.observe(this.stickySentinel);

      this.updateStickyState();
    }

    getStickyTopOffsetCSSValue() {
      const hostStyles = getComputedStyle(this.getStickyHost());
      const hostOffset = hostStyles.getPropertyValue("--article-toc-sticky-top").trim();
      const hostParsed = Number.parseFloat(hostOffset);

      if (Number.isFinite(hostParsed) && hostParsed > 0) {
        return hostParsed;
      }

      const rootStyles = getComputedStyle(document.documentElement);
      const rootOffset = rootStyles.getPropertyValue("--article-toc-sticky-top").trim();
      const rootParsed = Number.parseFloat(rootOffset);

      return Number.isFinite(rootParsed) && rootParsed > 0 ? rootParsed : 0;
    }

    updateStickyState() {
      const stickyHost = this.getStickyHost();

      if (this.mediaQuery.matches) {
        stickyHost.classList.remove("is-sticky-active");
        return;
      }

      if (!this.stickySentinel) return;

      const topOffset = this.getStickyTopOffsetCSSValue();
      const sentinelTop = this.stickySentinel.getBoundingClientRect().top;
      const hostTop = stickyHost.getBoundingClientRect().top;
      const isBelowFold = sentinelTop > window.innerHeight;
      const passedOriginalPosition = sentinelTop <= topOffset;
      const isPinned = hostTop <= topOffset + 1;
      const isStuck = !isBelowFold && passedOriginalPosition && isPinned;

      stickyHost.classList.toggle("is-sticky-active", isStuck);
    }

    updateStickyTopOffset() {
      const offset = this.measureStickyTopOffset();
      const value = `${offset}px`;
      const stickyHost = this.getStickyHost();

      document.documentElement.style.setProperty("--article-toc-sticky-top", value);
      document.documentElement.style.setProperty("--sticky-header-offset", value);
      stickyHost.style.setProperty("--article-toc-sticky-top", value);
      this.style.setProperty("--article-toc-sticky-top", value);

      return offset;
    }

    isElementVisible(element) {
      if (!element) return false;

      const style = getComputedStyle(element);
      return style.display !== "none" && style.visibility !== "hidden" && element.offsetHeight > 0;
    }

    isHeaderHidden(element) {
      return (
        element.classList.contains("section-header") && element.classList.contains("section-header-hidden")
      );
    }

    getSectionHeaderElement() {
      return document.querySelector('[id^="shopify-section-"][class*="section-header"]');
    }

    getHeaderChromeElements() {
      const elements = [];

      document.querySelectorAll('[id^="shopify-section-"].shopify-section-group-header-group').forEach((section) => {
        if (this.isElementVisible(section)) {
          elements.push(section);
        }
      });

      document.querySelectorAll(".section-breadcrumbs-wrapper").forEach((element) => {
        if (this.isElementVisible(element)) {
          elements.push(element);
        }
      });

      return elements;
    }

    measureStickyTopOffset() {
      let offset = 0;

      this.getHeaderChromeElements().forEach((element) => {
        if (this.isHeaderHidden(element)) return;

        const rect = element.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0) return;

        if (rect.top <= 8) {
          offset = Math.max(offset, Math.ceil(rect.bottom));
        }
      });

      if (offset > 0) {
        return offset;
      }

      const sectionHeader = this.getSectionHeaderElement();
      if (!sectionHeader || this.isHeaderHidden(sectionHeader)) {
        return 0;
      }

      const isStickyHeader =
        sectionHeader.classList.contains("section-header-sticky-always") ||
        sectionHeader.classList.contains("section-header-sticky");

      if (!isStickyHeader) {
        return 0;
      }

      const stickyHeader = sectionHeader.querySelector("sticky-header:not([disabled])");
      if (stickyHeader?.offsetHeight > 0) {
        return stickyHeader.offsetHeight;
      }

      if (sectionHeader.offsetHeight > 0) {
        return sectionHeader.offsetHeight;
      }

      return 0;
    }

    setupScrollSpy(headings) {
      const links = this.querySelectorAll(".js-article-toc-link");
      if (!headings.length || !links.length) return;

      this.headingsForSpy = headings;
      this.scrollObserver?.disconnect();

      if (this.onScrollSpyScroll) {
        window.removeEventListener("scroll", this.onScrollSpyScroll);
        this.onScrollSpyScroll = null;
      }

      if (this.mediaQuery.matches) {
        this.setupDesktopScrollSpy(headings);
        return;
      }

      this.setupMobileScrollSpy(headings);
    }

    setupDesktopScrollSpy(headings) {
      const visibility = new Map();

      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visibility.set(entry.target, entry);
          });

          const visible = [...visibility.values()]
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          let activeHeading = visible.length ? visible[0].target : null;

          if (!activeHeading && this.lastActiveHeading) {
            activeHeading = this.lastActiveHeading;
          } else if (!activeHeading) {
            activeHeading = this.getActiveHeadingAtScrollOffset(headings, true);
          }

          if (activeHeading) {
            this.setActiveState(activeHeading);
          }
        },
        {
          rootMargin: "-20% 0px -70% 0px",
          threshold: [0, 0.25, 0.5, 1],
        },
      );

      headings.forEach((heading) => this.scrollObserver.observe(heading));
    }

    setupMobileScrollSpy(headings) {
      this.onScrollSpyScroll = () => {
        if (this.scrollSpyRaf) return;
        this.scrollSpyRaf = requestAnimationFrame(() => {
          this.scrollSpyRaf = 0;
          this.syncActiveHeadingFromScroll();
        });
      };

      window.addEventListener("scroll", this.onScrollSpyScroll, { passive: true });
      this.syncActiveHeadingFromScroll();
    }

    getActiveHeadingAtScrollOffset(headings, isDesktop = this.mediaQuery.matches) {
      const { total } = this.getScrollOffsetForTarget(isDesktop);
      let activeHeading = headings[0] || null;

      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= total + 1) {
          activeHeading = heading;
          continue;
        }

        break;
      }

      return activeHeading;
    }

    isHeadingAlignedAtReadLine(heading, isDesktop = false) {
      if (!heading?.isConnected) return false;

      const { total } = this.getScrollOffsetForTarget(isDesktop);
      const top = heading.getBoundingClientRect().top;

      return top <= total + 12 && top >= total - 48;
    }

    confirmActiveStateAfterScroll(scrollTarget) {
      if (!this.headingsForSpy.length || this.mediaQuery.matches) return;

      const scrollTolerance = 8;
      let activeHeading = this.getActiveHeadingAtScrollOffset(this.headingsForSpy, false);

      if (scrollTarget?.isConnected) {
        const expectedScrollTop = this.getScrollTopForHeading(scrollTarget, false);
        const scrollNearTarget = Math.abs(window.scrollY - expectedScrollTop) <= scrollTolerance;

        if (scrollNearTarget || this.isHeadingAlignedAtReadLine(scrollTarget, false)) {
          activeHeading = scrollTarget;
        }
      }

      if (activeHeading) {
        this.setActiveState(activeHeading);
      }
    }

    syncActiveHeadingFromScroll() {
      if (!this.headingsForSpy.length || this.mediaQuery.matches) return;

      if (this.scrollClickLock?.heading) {
        this.setActiveState(this.scrollClickLock.heading);
        return;
      }

      const activeHeading = this.getActiveHeadingAtScrollOffset(this.headingsForSpy, false);
      if (activeHeading) {
        this.setActiveState(activeHeading);
      }
    }

    setActiveState(activeHeading) {
      if (!activeHeading?.isConnected) return;

      this.lastActiveHeading = activeHeading;
      this.clearActiveLinks();
      this.setActiveLink(activeHeading.id);
      this.updateDynamicHeading(activeHeading);
    }

    clearActiveLinks() {
      this.querySelectorAll(".js-article-toc-link").forEach((link) => {
        link.classList.remove("is-active");
      });
    }

    setActiveLink(activeId) {
      if (!activeId) return;

      this.querySelectorAll(".js-article-toc-link").forEach((link) => {
        link.classList.toggle("is-active", link.getAttribute("href") === `#${activeId}`);
      });
    }

    getActiveHeadingText(activeHeading) {
      if (!activeHeading) return "";
      return activeHeading.textContent.replace(/\s+/g, " ").trim();
    }

    updateDynamicHeading(activeHeading) {
      if (!this.headingEl) return;

      if (this.mediaQuery.matches) {
        this.headingEl.textContent = this.defaultHeading;
        if (this.navEl) {
          this.navEl.setAttribute("aria-label", this.defaultHeading);
        }
        return;
      }

      const headingText = this.getActiveHeadingText(activeHeading);
      const displayHeading = headingText || this.defaultHeading;

      this.headingEl.textContent = displayHeading;

      if (this.navEl) {
        this.navEl.setAttribute("aria-label", displayHeading);
      }
    }

    hide() {
      this.hidden = true;
      if (this.stickyHost) {
        this.stickyHost.hidden = true;
      }
    }
  }

  customElements.define("article-table-of-contents", ArticleTableOfContents);
})();
